import {useCallback, useEffect, useMemo, useState} from 'react';
import {buildUnifiedPatientList, ehrService} from '../../services/ehrService';
import {
  fetchUnifiedPatientsView,
  UnifiedPatientsViewResponse,
  UnifiedPatientViewRow,
} from '../../services/viewsClient';
import {
  isMockEhrEnabled,
  MOCK_EHR_CONFIG,
  MOCK_EHR_PATIENTS,
  MOCK_UNIFIED_PATIENTS,
} from '../../mocks/mockEhrFixtures';

// Local-roster window the hook loaded before the view migration
// (patientService PAGE_SIZE). The view windows local and cached-EHR rows
// independently by this limit, so the local side keeps the same effective
// page size.
const VIEW_PAGE_LIMIT = 20;

const EMPTY_STATE: Record<string, any> = {
  patients: [],
  unifiedPatients: [],
  ehrConfig: null,
  hasActiveEhr: false,
  rosterCount: null,
  isLoading: false,
  hasLoaded: false,
  hasLoadedEhrConfig: false,
  error: null,
};

const toDateOrNull = (iso: unknown): Date | null => {
  if (!iso) return null;
  const d = new Date(iso as string);
  return Number.isNaN(d.getTime()) ? null : d;
};

// View row → the legacy snake_case Patient doc shape every picker/roster
// surface renders (mirror of dataLayerClient.patientRosterRowToLegacyPatient-
// Doc, inlined so the hook's service imports stay mock-friendly in consumer
// tests). Rows the server merged through an explicit EHR link additionally
// surface the EHR-side id under the legacy `fhir_patient_id` field so
// EHR-link resolution (ehrService.resolveEhrPatientId, planner identity/rank
// keys) keeps working.
function viewLocalToLegacyPatientDoc(
  row: UnifiedPatientViewRow,
): Record<string, any> | null {
  const local = row.local;
  if (!local) return null;
  return {
    id: local.patientId,
    first_name: local.firstName || '',
    last_name: local.lastName || '',
    preferred_language: local.preferredLanguage ?? null,
    preferred_dialect: local.preferredDialect ?? null,
    date_of_birth: local.dateOfBirth ?? null,
    gender: local.gender ?? null,
    mrn: local.mrn ?? null,
    phone_number: local.phoneNumber ?? null,
    email: local.email ?? null,
    patientEHRLinkId: local.patientEHRLinkId ?? null,
    ...(row.matchKind === 'linked' && row.ehr?.ehrPatientId
      ? {fhir_patient_id: row.ehr.ehrPatientId}
      : {}),
    created_at: toDateOrNull(local.createdAt),
    updated_at: toDateOrNull(local.updatedAt),
    _v2: true,
  };
}

// View row → the ehrService.mapEHRPatient projection consumers already
// render. `id` / `ehrSourceId` / `ehr_source_id` all carry the external EHR
// patient id (readers probe them in that order); the server already applied
// the eCW mrn→accountNo fallback to `mrn`.
function viewEhrToMappedEhrPatient(
  row: UnifiedPatientViewRow,
): Record<string, any> | null {
  const ehr = row.ehr;
  if (!ehr) return null;
  return {
    id: ehr.ehrPatientId,
    cacheDocumentId: ehr.cacheDocumentId ?? null,
    ehrSourceId: ehr.ehrPatientId,
    ehr_source_id: ehr.ehrPatientId,
    ehrType: ehr.ehrType ?? null,
    firstName: ehr.firstName ?? null,
    lastName: ehr.lastName ?? null,
    middleName: ehr.middleName ?? null,
    displayName: ehr.displayName ?? null,
    fullName: ehr.fullName ?? null,
    patientName: ehr.displayName || ehr.fullName || null,
    dob: ehr.dob ?? null,
    gender: ehr.gender ?? null,
    email: ehr.email ?? null,
    phone: ehr.phone ?? null,
    mrn: ehr.mrn ?? null,
    accountNo: ehr.accountNo ?? null,
    language: ehr.language ?? null,
    pcp: ehr.pcp ?? null,
    pcpId: ehr.pcpId ?? null,
  };
}

// Server matchKind/mergedBy → the client's legacy PatientItem kinds:
//   'local' with no EHR side                        → 'localOnly'
//   'local' with an EHR side (mergedBy=demographics) → 'mergedByExactName'
//   'linked' (mergedBy=ehrLink)                      → 'mergedByLinkedEhr'
//   'ehr'                                            → 'ehrOnly'
function legacyMatchKind(row: UnifiedPatientViewRow): string {
  if (row.matchKind === 'linked') return 'mergedByLinkedEhr';
  if (row.matchKind === 'ehr') return 'ehrOnly';
  return row.ehr ? 'mergedByExactName' : 'localOnly';
}

/**
 * One patients-unified view row → the PatientItem shape every picker surface
 * renders ({id, localPatient, ehrPatient, matchKind, displayName, sortName} —
 * what ehrService.buildUnifiedPatientList produced client-side before the
 * default list moved to the backend view). Exported for tests.
 */
export function unifiedViewRowToPatientItem(
  row: UnifiedPatientViewRow,
): Record<string, any> {
  return {
    id: row.id,
    localPatient: viewLocalToLegacyPatientDoc(row),
    ehrPatient: viewEhrToMappedEhrPatient(row),
    matchKind: legacyMatchKind(row),
    displayName: row.displayName,
    sortName: row.sortName,
  };
}

// The legacy EhrConfig-doc fields consumers actually read from this hook:
// `status` drives ehrService.isConfigActive, `ehr_type` mirrors the doc
// field, and `timeZone` feeds ehrService.resolveTimeZone (appointment
// now/soon math). No config doc server-side (ehr disabled with no type)
// maps to null like the old fetchEhrConfig miss.
function ehrConfigFromView(
  response: UnifiedPatientsViewResponse,
): Record<string, any> | null {
  const enabled = Boolean(response?.ehr?.enabled);
  const type = response?.ehr?.type ?? null;
  if (!enabled && !type) return null;
  return {
    status: enabled ? 'active' : null,
    ehr_type: type,
    timeZone: response?.facilityTimeZone || null,
    _v2View: true,
  };
}

// Shared loader/adapter behind every patient-picker surface. The default
// list (local roster + cached-EHR merge + EHR config) is composed by the
// backend patients-unified view in ONE call; surfaces that pass their live
// local roster in still get EHR-config resolution and client-side
// unified-list construction over that override.
export default function useUnifiedPatients({
  userData,
  enabled = true,
  fetchLocalPatients = true,
  localPatients = null,
  ehrConfig: ehrConfigOverride,
  hasActiveEhr: hasActiveEhrOverride,
  // Fetch the cached EHR roster size when the integration is active. The
  // count is informational ("your roster has N patients"); failures just
  // leave it null so the hint hides.
  withRosterCount = false,
  // Pickers inside modals keep their last result while closed; the
  // always-mounted workspace hooks reset when the user context goes away.
  clearWhenDisabled = false,
}: Record<string, any> = {}): Record<string, any> {
  const facilityId = userData?.facilityId || null;
  const userId = userData?.userId || null;
  const companyId = userData?.companyId || null;
  const isMockEhrMode = isMockEhrEnabled(userData);
  const hasLocalPatientsOverride = Array.isArray(localPatients);
  const shouldLoadLocalPatients = Boolean(
    fetchLocalPatients && facilityId && userId,
  );
  const shouldLoadEhrConfig = Boolean(
    companyId &&
    typeof hasActiveEhrOverride !== 'boolean' &&
    typeof ehrConfigOverride === 'undefined',
  );

  const [state, setState] = useState(EMPTY_STATE);
  const [loadKey, setLoadKey] = useState(0);
  const reload = useCallback(() => setLoadKey(key => key + 1), []);

  useEffect(() => {
    if (!enabled || (fetchLocalPatients && (!facilityId || !userId))) {
      if (clearWhenDisabled) setState(EMPTY_STATE);
      return undefined;
    }

    if (isMockEhrMode) {
      setState({
        patients: [],
        unifiedPatients: [...MOCK_UNIFIED_PATIENTS],
        ehrConfig: MOCK_EHR_CONFIG,
        hasActiveEhr: true,
        rosterCount: withRosterCount ? MOCK_EHR_PATIENTS.length : null,
        isLoading: false,
        hasLoaded: true,
        hasLoadedEhrConfig: true,
        error: null,
      });
      return undefined;
    }

    if (!shouldLoadLocalPatients && !shouldLoadEhrConfig) {
      setState(prev => {
        if (prev.hasLoaded && !prev.isLoading && !prev.error) return prev;
        return {
          ...prev,
          ehrConfig: ehrConfigOverride || prev.ehrConfig,
          hasActiveEhr:
            typeof hasActiveEhrOverride === 'boolean'
              ? hasActiveEhrOverride
              : prev.hasActiveEhr,
          isLoading: false,
          hasLoaded: true,
          hasLoadedEhrConfig: true,
          error: null,
        };
      });
      return undefined;
    }

    let isActive = true;
    const controller = new AbortController();
    setState(prev => ({...prev, isLoading: true, error: null}));

    (async () => {
      try {
        // ONE backend call replaces the old patientService.fetchPatients
        // roster page + ehrService.fetchEhrConfig pair (and the client-side
        // default-list merge). The legacy config-only path (roster passed in
        // by the caller) still resolves the EHR flags from the same view,
        // just without pulling a roster window.
        const response = await fetchUnifiedPatientsView({
          limit: shouldLoadLocalPatients ? VIEW_PAGE_LIMIT : 1,
          signal: controller.signal,
        });
        if (!isActive) return;

        const ehrConfig =
          typeof ehrConfigOverride === 'undefined'
            ? ehrConfigFromView(response)
            : ehrConfigOverride || null;
        // Parity with the legacy derivation: an explicit boolean override
        // wins, an explicit config override is interrogated like the old
        // fetched doc, otherwise the server's enabled flag (same
        // status==='active' check, evaluated backend-side) is authoritative.
        const hasActiveEhr =
          typeof hasActiveEhrOverride === 'boolean'
            ? hasActiveEhrOverride
            : typeof ehrConfigOverride !== 'undefined'
              ? Boolean(
                  ehrConfigOverride &&
                  ehrService.isConfigActive(ehrConfigOverride),
                )
              : Boolean(response?.ehr?.enabled);
        const unifiedPatients = shouldLoadLocalPatients
          ? (response?.patients || []).map(unifiedViewRowToPatientItem)
          : null;

        setState(prev => ({
          patients: unifiedPatients
            ? unifiedPatients.map(item => item.localPatient).filter(Boolean)
            : prev.patients,
          unifiedPatients: unifiedPatients ?? prev.unifiedPatients,
          ehrConfig,
          hasActiveEhr,
          rosterCount: null,
          isLoading: false,
          hasLoaded: true,
          hasLoadedEhrConfig: true,
          error: null,
        }));

        if (hasActiveEhr && withRosterCount && companyId) {
          ehrService
            .getCachedPatientCount(companyId)
            .then(count => {
              if (isActive) {
                setState(prev => ({
                  ...prev,
                  rosterCount: typeof count === 'number' ? count : null,
                }));
              }
            })
            .catch(() => {
              if (isActive) setState(prev => ({...prev, rosterCount: null}));
            });
        }
      } catch (error) {
        if (!isActive) return;
        console.error('Failed to load patient roster:', error);
        setState(prev => ({
          ...EMPTY_STATE,
          // A roster failure shouldn't un-resolve an EHR config that already
          // landed — keep the mode decision stable.
          ehrConfig: prev.ehrConfig,
          hasActiveEhr: prev.hasActiveEhr,
          hasLoaded: true,
          hasLoadedEhrConfig: true,
          error,
        }));
      }
    })();

    return () => {
      isActive = false;
      controller.abort();
    };
  }, [
    enabled,
    fetchLocalPatients,
    facilityId,
    userId,
    companyId,
    withRosterCount,
    clearWhenDisabled,
    loadKey,
    shouldLoadLocalPatients,
    shouldLoadEhrConfig,
    ehrConfigOverride,
    hasActiveEhrOverride,
    isMockEhrMode,
  ]);

  const patients = hasLocalPatientsOverride ? localPatients : state.patients;
  const ehrConfig =
    typeof ehrConfigOverride === 'undefined'
      ? state.ehrConfig
      : ehrConfigOverride;
  const hasActiveEhr =
    typeof hasActiveEhrOverride === 'boolean'
      ? hasActiveEhrOverride
      : state.hasActiveEhr;
  const hasLoaded =
    state.hasLoaded ||
    (enabled && !shouldLoadLocalPatients && !shouldLoadEhrConfig);
  // Config and roster arrive on the same view call now, so this settles with
  // `hasLoaded` (or immediately when an override supplied the flags) — mode
  // gates should still key off it.
  const hasLoadedEhrConfig = state.hasLoadedEhrConfig || hasLoaded;

  // Client-side merge kept ONLY for live-search results: deep/typeahead EHR
  // search hits still arrive on their existing routes and get merged into
  // the local roster here. The DEFAULT list is server-composed by the
  // patients-unified view (see `unifiedPatients` below).
  const buildUnifiedPatients = useCallback(
    (ehrPatients = []) => {
      if (isMockEhrMode) return [...MOCK_UNIFIED_PATIENTS];
      return (
        buildUnifiedPatientList(patients, hasActiveEhr ? ehrPatients : []) || []
      );
    },
    [patients, hasActiveEhr, isMockEhrMode],
  );

  // Server-composed default list. Callers that pass their live local roster
  // in keep the legacy client-side construction over that override.
  const unifiedPatients = useMemo(
    () =>
      isMockEhrMode || hasLocalPatientsOverride
        ? buildUnifiedPatients()
        : state.unifiedPatients,
    [
      isMockEhrMode,
      hasLocalPatientsOverride,
      buildUnifiedPatients,
      state.unifiedPatients,
    ],
  );

  return {
    ...state,
    patients,
    ehrConfig,
    hasActiveEhr,
    hasLoaded,
    hasLoadedEhrConfig,
    unifiedPatients,
    buildUnifiedPatients,
    reload,
  };
}
